/**
 * EditCallBackContactView — modal sub-view for editing an emergency
 * contact from the appointment-context home-visit picker.
 *
 * Opened when the user taps Edit on a picker card where the underlying
 * entry is an emergency contact (`isResidential === false`). Mirrors
 * Flutter `EditCallBackContactWidget`
 * (`scheduling_module/.../edit_callback_contact_widget.dart`).
 *
 * Composes the same shared `EmergencyContactEditFields` as the patient-
 * chart inline editor (`emergency-contact-row.tsx`'s `EditCard`); differs
 * in chrome (Back / "Create contact" / Save header — Flutter uses the
 * same title even in edit mode, line `:105`) and save semantics (commits
 * immediately via `useSavePatientEmergencyContactList` instead of
 * deferring to a tab-level Save).
 *
 * Wire path note (deliberate React vs. Flutter divergence): Flutter's
 * EditCallBackContactWidget fires `UpdatePatientEvent` which routes
 * through `PUT /api/Patients/{id}` with the modified emergency-contact
 * list inside the patient body. React's `useUpdatePatient` strips
 * `emergencyContactResponse` from the PUT body by design (PROD-728
 * convention) — emergency contacts ship through the separate
 * `POST /api/Patients/EmergencyContact?PatientId=` endpoint. Both are
 * dev-supported endpoints; we use the POST path here for consistency
 * with the patient-chart tab.
 *
 * Cross-domain import note (Andrew override 2026-05-20): lives under
 * `src/patients/` but is imported by `src/scheduling/`. Same rationale
 * as `edit-residential-address-view.tsx`.
 */

import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { V2_CARD_EDGE, V2_PANEL_TITLE, v2Look } from '@/shared/ui/design-v2';
import { toast } from 'sonner';
import { Button } from '@scripsteam/scrips-signal-ds';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import {
  useEmergencyContacts,
  useSavePatientEmergencyContactList,
} from '@/patients/services/use-emergency-contacts';
import {
  rowFromServer,
  rowToServer,
} from './emergency-contacts-section';
import {
  EmergencyContactEditFields,
  isEmergencyContactSaveEnabled,
} from './emergency-contact-edit-fields';
import type { EmergencyContactRow } from './emergency-contact-schema';
import type { EmergencyContactList } from '@/types/patient-api';

export interface EditCallBackContactViewProps {
  patientId: string;
  /**
   * The id of the emergency-contact row the appointment is currently
   * pointing at. Matches an `emergencyContactId` on the patient's
   * emergency-contact list.
   */
  emergencyContactId: string;
  onCancel: () => void;
  /** Called after a successful POST 200. Caller refreshes the picker. */
  onSave: (saved: EmergencyContactList) => void;
}

export function EditCallBackContactView({
  patientId,
  emergencyContactId,
  onCancel,
  onSave,
}: EditCallBackContactViewProps) {
  // PROD-2743 · switch ON: DS `back` glyph, the title on the DS panel-title step in text-primary
  // (not brand ink), the form card with the card border + resting shadow-2. OFF = today.
  const { data: contacts, isLoading } = useEmergencyContacts(patientId);
  const saveMutation = useSavePatientEmergencyContactList();

  const [row, setRow] = useState<EmergencyContactRow | null>(null);

  // Find the matching server row once contacts land.
  const matchedServer = useMemo(() => {
    if (!contacts) return null;
    return (
      contacts.find(
        (c): c is EmergencyContactList =>
          !!c &&
          (c.emergencyContactId ?? '').toLowerCase() ===
            emergencyContactId.toLowerCase(),
      ) ?? null
    );
  }, [contacts, emergencyContactId]);

  useEffect(() => {
    if (!matchedServer || row) return;
    setRow({ ...rowFromServer(matchedServer), isEdit: true });
  }, [matchedServer, row]);

  const handleSave = () => {
    if (!contacts || !row || !isEmergencyContactSaveEnabled(row)) return;

    // Project the full list, replacing the matched entry with the edited row.
    const nextList: EmergencyContactList[] = (contacts ?? [])
      .filter((c): c is EmergencyContactList => !!c)
      .map((c) => {
        if (
          (c.emergencyContactId ?? '').toLowerCase() ===
          emergencyContactId.toLowerCase()
        ) {
          return rowToServer(row, patientId);
        }
        return c;
      });

    saveMutation.mutate(
      { patientId, contacts: nextList },
      {
        onSuccess: (savedList) => {
          toast.success('Patient Data Updated Successfully');
          const saved =
            savedList.find(
              (s) =>
                (s.emergencyContactId ?? '').toLowerCase() ===
                emergencyContactId.toLowerCase(),
            ) ?? rowToServer(row, patientId);
          onSave(saved);
        },
      },
    );
  };

  // Flutter uses "Create contact" as the title even in edit mode
  // (`edit_callback_contact_widget.dart:105`). Mirrored verbatim.
  const title = 'Create contact';
  const saveEnabled =
    !!row && isEmergencyContactSaveEnabled(row) && !saveMutation.isPending && !isLoading;
  const notFound = !isLoading && !!contacts && !matchedServer;

  return (
    <div className="flex flex-col h-full bg-surface-card">
      <div className="flex items-center justify-between px-4 h-14 bg-surface-card border-b border-border-default sticky top-0 z-10">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1 text-sm font-medium text-brand-primary hover:underline"
        >
          <ChevronLeft className="size-4" />
          Back
        </button>
        <p className={v2Look('text-base font-bold text-brand-depth', V2_PANEL_TITLE)}>{title}</p>
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={!saveEnabled || notFound}
        >
          Save
        </Button>
      </div>

      <div className="flex-1 overflow-auto scrollbar-hide p-4">
        <div className={v2Look('rounded-lg border border-border-default bg-surface-card p-6', V2_CARD_EDGE)}>
          {isLoading || !row ? (
            notFound ? (
              <p className="text-sm text-text-muted text-center">
                Contact not found for selected patient
              </p>
            ) : (
              <div className="space-y-6">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-2/3" />
              </div>
            )
          ) : (
            <EmergencyContactEditFields row={row} onChange={setRow} />
          )}
        </div>
      </div>
    </div>
  );
}
